// @noflow
import { Allergen, HealthIssue } from '@/gql/graphql'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import truncateText from '@/utils/truncateText'

import BREAKPOINTS from '@/constants/Breakpoints'

import useWindowSize from '@/hooks/useWindowSize'

import {
  WizardAge,
  WizardEaterType
} from '@/components/pages/SignupWizardPage/types/types'

import STYLES from './PreWizardGuestDogProfileCard.module.sass'

import Image from '../Image/Image'
import Text from '../Text/Text'

type Props = {
  dogName: string
  dogAge: WizardAge
  breedName: string
  weight: number
  appetite: WizardEaterType
  allergies: Array<Allergen> | null
  healthIssues: Array<HealthIssue> | null
  maxTextLength?: number
  namespace: string
}

const PreWizardGuestDogProfileCard = ({
  dogName,
  dogAge,
  breedName,
  weight,
  appetite,
  allergies,
  healthIssues,
  maxTextLength = 110,
  namespace
}: Props): JSX.Element | null => {
  const { t } = useTranslation(namespace)
  const { windowWidth } = useWindowSize()
  const copyContext = 'returning_guest.card'

  const getDogAge = useCallback(() => {
    if (dogAge.years && dogAge.years > 0) {
      return t(`${copyContext}.age_years_and_breed`, {
        ageInYears: dogAge.years,
        breedName
      })
    } else if (dogAge.months && dogAge.months > 0) {
      return t(`${copyContext}.age_months_and_breed`, {
        ageInMonths: dogAge.months,
        breedName
      })
    } else if (dogAge.weeks && dogAge.weeks > 0) {
      return t(`${copyContext}.age_weeks_and_breed`, {
        ageInWeeks: dogAge.weeks,
        breedName
      })
    } else return ''
  }, [dogAge, breedName, t])

  const getAppetite = useCallback(() => {
    switch (appetite) {
      case 'eatsAnything':
        return t(`${copyContext}.appetite_foodie`)
      case 'goodEater':
        return t(`${copyContext}.appetite_good_eater`)
      case 'veryFussy':
        return t(`${copyContext}.appetite_very_fussy`)
      case 'canBeFussy':
        return t(`${copyContext}.appetite_fussy`)
      default:
        return ''
    }
  }, [appetite, t])

  const getHealthIssues = useCallback(() => {
    if (healthIssues && healthIssues.length > 0) {
      const healthIssuesText = t(`${copyContext}.health_issues`, {
        healthIssues: healthIssues.map((issue) => issue.name).join(', ')
      })
      return truncateText(healthIssuesText, maxTextLength)
    }
    return t(`${copyContext}.health_issues_none`)
  }, [healthIssues, maxTextLength, t])

  const getAllergies = useCallback(() => {
    if (allergies && allergies.length > 0) {
      const allergiesText = t(`${copyContext}.allergies`, {
        allergies: allergies.map((allergen) => allergen.name).join(', ')
      })
      return truncateText(allergiesText, maxTextLength)
    }
    return t(`${copyContext}.allergies_none`)
  }, [allergies, maxTextLength, t])

  return (
    <div className={STYLES.card}>
      <div className={STYLES.yellowCard}>
        <Image
          alt={t(`${copyContext}.dog_image_alt`)}
          slug="returning-guest-dog"
          image={{
            width: windowWidth > BREAKPOINTS.md ? 64 : 56,
            height: windowWidth > BREAKPOINTS.md ? 64 : 56,
            resizeMode: 'resize_to_fit'
          }}
        />
        <div className={STYLES.dogNameTape}>
          <Text
            translate={false}
            text={dogName}
            variant="textRegular20"
            margin={false}
            bold
          />
        </div>
        <Text
          text={getDogAge()}
          translate={false}
          variant="textRegular16"
          margin={false}
        />
      </div>
      <div className={STYLES.content}>
        <div className={STYLES.row}>
          <Image
            alt=""
            slug="returning-guest-scales-yellow"
            image={{
              width: 32,
              height: 32,
              resizeMode: 'resize_to_fit'
            }}
          />
          <Text
            namespace={namespace}
            text={`${copyContext}.weighs`}
            variables={{
              weight: weight / 1000
            }}
            variant="textRegular16"
            margin={false}
          />
        </div>
        <div className={STYLES.row}>
          <Image
            alt=""
            slug="returning-guest-bone-yellow"
            image={{
              width: 32,
              height: 32,
              resizeMode: 'resize_to_fit'
            }}
          />
          <Text
            text={getAppetite()}
            variant="textRegular16"
            margin={false}
            translate={false}
          />
        </div>
        <div className={STYLES.row}>
          <Image
            alt=""
            slug="returning-guest-nuts-yellow"
            image={{
              width: 32,
              height: 32,
              resizeMode: 'resize_to_fit'
            }}
          />
          <Text
            translate={false}
            text={getAllergies()}
            variant="textRegular16"
            margin={false}
          />
        </div>
        <div className={STYLES.row}>
          <Image
            alt=""
            slug="returning-guests-health-yellow"
            image={{
              width: 32,
              height: 32,
              resizeMode: 'resize_to_fit'
            }}
          />
          <Text
            translate={false}
            text={getHealthIssues()}
            variant="textRegular16"
            margin={false}
          />
        </div>
      </div>
    </div>
  )
}

export default PreWizardGuestDogProfileCard
